// src/layouts/SharedComponents/AvatarDicebearModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Avatar, Modal, Segmented, Spin, Tooltip } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import translateX from 'utils/translateX';
import attachmentsService from 'services/attachmentsService';
import appService from 'services/appService';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const DICEBEAR_API_BASE = 'https://api.dicebear.com/9.x';

const AVATAR_STYLES = [
  'adventurer',
  'adventurer-neutral',
  'avataaars',
  'avataaars-neutral',
  'big-ears',
  'big-ears-neutral',
  'big-smile',
  'bottts',
  'bottts-neutral',
  'croodles',
  'croodles-neutral',
  'dylan',
  'fun-emoji',
  'glass',
  'icons',
  'identicon',
  'lorelei',
  'lorelei-neutral',
  'micah',
  'miniavs',
  'notionists',
  'notionists-neutral',
  'open-peeps',
  'personas',
  'pixel-art',
  'pixel-art-neutral',
  'rings',
  'shapes',
  'thumbs',
];

// 40 seeds = 8 rows x 5 columns (reduced to avoid rate limiting: 50 req/s max)
const AVATAR_SEEDS = [
  // Row 1-4
  'alpha',
  'bravo',
  'charlie',
  'delta',
  'echo',
  'foxtrot',
  'golf',
  'hotel',
  'india',
  'juliet',
  'kilo',
  'lima',
  'mike',
  'november',
  'oscar',
  'papa',
  'quebec',
  'romeo',
  'sierra',
  'tango',
  // Row 5-8
  'uniform',
  'victor',
  'whiskey',
  'xray',
  'yankee',
  'zulu',
  'apollo',
  'atlas',
  'bolt',
  'comet',
  'nova',
  'onyx',
  'pulse',
  'phoenix',
  'quantum',
  'rocket',
  'spark',
  'titan',
  'nebula',
  'orbit',
];

// Grid config
const GRID_COLUMNS_MOBILE = 4;
const GRID_COLUMNS_DESKTOP = 5;
const VISIBLE_ROWS = 4;
const AVATAR_SIZE_MOBILE = 68;
const AVATAR_SIZE_DESKTOP = 72;
const AVATAR_GAP = 12;

// Detect if device is mobile/tablet (touch device)
const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  );
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const AvatarDicebearModal = ({ open, onCancel, onConfirm }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [selectedStyle, setSelectedStyle] = useState('bottts');
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [loadingImages, setLoadingImages] = useState({});
  const [failedImages, setFailedImages] = useState({});
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const gridRef = useRef(null);
  const isConfirmingRef = useRef(false);
  const selectedAvatarRef = useRef(null);

  // Detect device type for responsive grid
  const isMobile = useMemo(() => isMobileDevice(), []);
  const gridColumns = isMobile ? GRID_COLUMNS_MOBILE : GRID_COLUMNS_DESKTOP;
  const avatarSize = isMobile ? AVATAR_SIZE_MOBILE : AVATAR_SIZE_DESKTOP;

  // Keep ref in sync with state for use in callbacks
  selectedAvatarRef.current = selectedAvatar;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. MEMOS
  // ─────────────────────────────────────────────────────────────────────────────
  const avatarUrls = useMemo(() => {
    return AVATAR_SEEDS.map(seed => ({
      seed,
      url: `${DICEBEAR_API_BASE}/${selectedStyle}/svg?seed=${seed}`,
    }));
  }, [selectedStyle]);

  // Build Segmented options with avatar preview for each style
  const styleOptions = useMemo(() => {
    return AVATAR_STYLES.map(style => ({
      value: style,
      label: (
        <Tooltip title={style} placement="bottom">
          <div style={{ padding: 4 }}>
            <Avatar
              size={32}
              src={`${DICEBEAR_API_BASE}/${style}/svg?seed=preview`}
              style={{ background: '#f5f5f5' }}
            />
          </div>
        </Tooltip>
      ),
    }));
  }, []);

  // Filtered avatars (excluding failed ones)
  const visibleAvatars = useMemo(() => {
    return avatarUrls.filter(avatar => !failedImages[avatar.seed]);
  }, [avatarUrls, failedImages]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleStyleChange = value => {
    setSelectedStyle(value);
    setSelectedAvatar(null);
    setFocusedIndex(-1);
    setLoadingImages({});
    setFailedImages({});
  };

  const handleAvatarClick = (avatar, index) => {
    setSelectedAvatar(avatar);
    setFocusedIndex(index);
  };

  const handleAvatarDoubleClick = avatar => {
    // Prevent double execution
    if (isConfirmingRef.current) return;
    isConfirmingRef.current = true;

    setSelectedAvatar(avatar);
    // Small delay to ensure state is updated before confirm
    setTimeout(() => {
      handleConfirm();
      isConfirmingRef.current = false;
    }, 50);
  };

  const handleConfirm = useCallback(async () => {
    const avatar = selectedAvatarRef.current;
    if (!avatar || !onConfirm) return;

    // Immediately show preview using DiceBear URL (instant feedback)
    onConfirm({
      previewUrl: avatar.url, // Temporary preview
      isUploading: true,
    });

    try {
      // Upload DiceBear avatar to Firebase Storage in background
      const result = await attachmentsService.uploadDicebearAvatar({
        dicebearUrl: avatar.url,
      });

      if (result?.attachment) {
        const { storageUrl, base64Thumb, _id: attachmentId } = result.attachment;

        // Update with final data after upload completes
        onConfirm({
          largeUrl: storageUrl,
          thumbUrl: storageUrl,
          base64Thumb,
          attachmentId,
          isUploading: false,
        });

        appService.message({ flag: 's', text: translateX('avatar_generated_successfully') });
      } else {
        // Upload failed - reset loading state
        onConfirm({ isUploading: false });
        appService.message({ flag: 'e', text: translateX('avatar_upload_failed') });
      }
    } catch (error) {
      console.error('[AvatarDicebearModal] Upload error:', error);
      // Upload failed - reset loading state
      onConfirm({ isUploading: false });
      appService.message({ flag: 'e', text: translateX('avatar_upload_failed') });
    }
  }, [onConfirm]);

  const handleCancel = useCallback(() => {
    setSelectedAvatar(null);
    setFocusedIndex(-1);
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  // Keyboard navigation handler
  const handleKeyDown = useCallback(
    e => {
      if (!open) return;

      const totalAvatars = visibleAvatars.length;
      if (totalAvatars === 0) return;

      let newIndex = focusedIndex;

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          newIndex = focusedIndex < 0 ? 0 : (focusedIndex + 1) % totalAvatars;
          break;
        case 'ArrowLeft':
          e.preventDefault();
          newIndex = focusedIndex < 0 ? 0 : (focusedIndex - 1 + totalAvatars) % totalAvatars;
          break;
        case 'ArrowDown':
          e.preventDefault();
          newIndex = focusedIndex < 0 ? 0 : Math.min(focusedIndex + gridColumns, totalAvatars - 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          newIndex = focusedIndex < 0 ? 0 : Math.max(focusedIndex - gridColumns, 0);
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedAvatarRef.current && !isConfirmingRef.current) {
            isConfirmingRef.current = true;
            handleConfirm();
            setTimeout(() => {
              isConfirmingRef.current = false;
            }, 100);
          }
          return;
        case 'Escape':
          handleCancel();
          return;
        default:
          return;
      }

      if (newIndex !== focusedIndex && newIndex >= 0 && newIndex < totalAvatars) {
        setFocusedIndex(newIndex);
        setSelectedAvatar(visibleAvatars[newIndex]);

        // Scroll the focused item into view
        const gridElement = gridRef.current;
        if (gridElement) {
          const avatarElements = gridElement.querySelectorAll('[data-avatar-item]');
          if (avatarElements[newIndex]) {
            avatarElements[newIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        }
      }
    },
    [open, focusedIndex, visibleAvatars, gridColumns, handleConfirm, handleCancel]
  );

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setFocusedIndex(-1);
      isConfirmingRef.current = false;
    }
  }, [open]);

  // Add keyboard listener when modal is open
  useEffect(() => {
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  const handleImageLoad = seed => {
    setLoadingImages(prev => ({ ...prev, [seed]: false }));
  };

  const handleImageLoadStart = seed => {
    setLoadingImages(prev => ({ ...prev, [seed]: true }));
  };

  const handleImageError = seed => {
    setLoadingImages(prev => ({ ...prev, [seed]: false }));
    setFailedImages(prev => ({ ...prev, [seed]: true }));
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={translateX('choose_an_avatar')}
      open={open}
      onCancel={handleCancel}
      onOk={handleConfirm}
      okText={translateX('confirm')}
      cancelText={translateX('cancel')}
      okButtonProps={{ disabled: !selectedAvatar }}
      width={480}
      centered
      mask={{ closable: false }}
    >
      {/* Style selector with horizontal scroll */}
      <div
        style={{
          marginBottom: 16,
          overflowX: 'auto',
          overflowY: 'hidden',
          whiteSpace: 'nowrap',
          paddingBottom: 8,
        }}
      >
        <Segmented
          value={selectedStyle}
          onChange={handleStyleChange}
          options={styleOptions}
          style={{ display: 'inline-flex' }}
        />
      </div>

      {/* Avatar grid: 4 columns mobile, 5 columns desktop, with scroll */}
      <div
        ref={gridRef}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${gridColumns}, ${avatarSize}px)`,
          gap: AVATAR_GAP,
          justifyContent: 'center',
          padding: '8px 0',
          maxHeight: VISIBLE_ROWS * (avatarSize + AVATAR_GAP) + 8,
          overflowY: 'auto',
        }}
      >
        {visibleAvatars.map((avatar, index) => {
          const isSelected = selectedAvatar?.seed === avatar.seed;
          const isFocused = focusedIndex === index;
          const isLoading = loadingImages[avatar.seed];

          return (
            <div
              key={avatar.seed}
              data-avatar-item
              onClick={() => handleAvatarClick(avatar, index)}
              onDoubleClick={() => handleAvatarDoubleClick(avatar)}
              style={{
                position: 'relative',
                width: avatarSize,
                height: avatarSize,
                borderRadius: 8,
                border: isSelected
                  ? '3px solid #1890ff'
                  : isFocused
                    ? '2px solid #1890ff'
                    : '2px solid #e8e8e8',
                cursor: 'pointer',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                boxShadow: isSelected ? '0 0 8px rgba(24, 144, 255, 0.5)' : 'none',
                outline: isFocused && !isSelected ? '2px solid rgba(24, 144, 255, 0.3)' : 'none',
                outlineOffset: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
              }}
              onMouseEnter={e => {
                if (!isSelected) {
                  e.currentTarget.style.border = '2px solid #1890ff';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }
              }}
              onMouseLeave={e => {
                if (!isSelected) {
                  e.currentTarget.style.border = '2px solid #e8e8e8';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              {isLoading && <Spin size="small" style={{ position: 'absolute' }} />}
              <img
                src={avatar.url}
                alt={`Avatar ${avatar.seed}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  opacity: isLoading ? 0 : 1,
                  transition: 'opacity 0.2s ease',
                }}
                onLoadStart={() => handleImageLoadStart(avatar.seed)}
                onLoad={() => handleImageLoad(avatar.seed)}
                onError={() => handleImageError(avatar.seed)}
              />
              {isSelected && (
                <div
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    color: '#1890ff',
                    fontSize: 18,
                    background: 'white',
                    borderRadius: '50%',
                    lineHeight: 1,
                  }}
                >
                  <CheckCircleFilled />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default AvatarDicebearModal;
